import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon, CheckCircleIcon, CreditCardIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { ImageUploader } from '../upload/ImageUploader';
import { MultipleImageUploader } from '../upload/MultipleImageUploader';
import { awsService } from '../../services/awsService';
import { productService, type CreateProductDto, type MediaDto } from '../../services/productService';
import { categoryService, Category } from '../../services/categoryService';
import { sectorService, Sector } from '../../services/sectorService';
import { ProductCategory, Sector as SectorEnum, CATEGORY_LABELS, SECTOR_LABELS } from '../../types';
import { useAuth } from '../../context/AuthContext';
import { sellerOnboardingService } from '../../services/sellerOnboardingService';

interface AddProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

// Helper function to create reverse mapping from label to enum
const createCategoryNameToEnum = (): Record<string, ProductCategory> => {
  const mapping: Record<string, ProductCategory> = {};
  Object.entries(CATEGORY_LABELS).forEach(([enumValue, label]) => {
    mapping[label] = enumValue as ProductCategory;
  });
  return mapping;
};

const createSectorNameToEnum = (): Record<string, SectorEnum> => {
  const mapping: Record<string, SectorEnum> = {};
  Object.entries(SECTOR_LABELS).forEach(([enumValue, label]) => {
    mapping[label] = enumValue as SectorEnum;
  });
  return mapping;
};

const categoryNameToEnum = createCategoryNameToEnum();
const sectorNameToEnum = createSectorNameToEnum();

export const AddProductModal: React.FC<AddProductModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [isLoadingSectors, setIsLoadingSectors] = useState(false);
  const [stripeAccountStatus, setStripeAccountStatus] = useState<any>(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    category: '',
    sector: '',
    tags: '',
  });

  const [files, setFiles] = useState({
    thumbnail: null as File | null,
    mainFile: null as File | null,
    preview: null as File | null,
    thumbnailKey: '',
    mainFileKey: '',
    previewKey: '',
  });

  const [uploadProgress, setUploadProgress] = useState({
    thumbnail: 0,
    mainFile: 0,
    preview: 0,
  });

  const [galleryKeys, setGalleryKeys] = useState<string[]>([]);

  const productId = React.useMemo(() => Math.random().toString(36).substring(7), []);

  const checkStripeAccount = React.useCallback(async () => {
    if (user?.role !== 'SELLER') return;
    
    try {
      const status = await sellerOnboardingService.getStripeAccountStatus();
      setStripeAccountStatus(status);
      
      // If no account or account not enabled, show warning
      if (!status.accountId || (status.status !== 'ENABLED' || !status.onboardingCompleted)) {
        // Don't block, but will show warning in UI
      }
    } catch (error) {
      
      console.error('Error checking Stripe account:', error);
    }
  }, [user]);

  // Check Stripe account status when modal opens (for sellers)
  useEffect(() => {
    if (isOpen && user?.role === 'SELLER') {
      checkStripeAccount();
    }
  }, [isOpen, user, checkStripeAccount]);

  // Fetch categories and sectors when modal opens
  useEffect(() => {
    if (isOpen) {
      const loadCategories = async () => {
        setIsLoadingCategories(true);
        try {
          const response = await categoryService.getAllCategories();
          setCategories(response.categories || []);
        } catch (error) {
          console.error('Error loading categories:', error);
          setCategories([]);
        } finally {
          setIsLoadingCategories(false);
        }
      };

      const loadSectors = async () => {
        setIsLoadingSectors(true);
        try {
          const response = await sectorService.getAllSectors();
          setSectors(response.sectors || []);
        } catch (error) {
          console.error('Error loading sectors:', error);
          setSectors([]);
        } finally {
          setIsLoadingSectors(false);
        }
      };

      loadCategories();
      loadSectors();
    }
  }, [isOpen]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const uploadThumbnail = async (file: File, onProgress?: (progress: number) => void) => {
    setUploadProgress((prev) => ({ ...prev, thumbnail: 0 }));
    const key = await awsService.uploadFile(
      file,
      `products/${productId}/thumbnail`,
      (progress) => {
        setUploadProgress((prev) => ({ ...prev, thumbnail: progress }));
        onProgress?.(progress);
      }
    );
    setFiles((prev) => ({ ...prev, thumbnail: file, thumbnailKey: key }));
    return key;
  };

  const uploadMainFile = async (file: File, onProgress?: (progress: number) => void) => {
    setUploadProgress((prev) => ({ ...prev, mainFile: 0 }));
    const key = await awsService.uploadFile(
      file,
      `products/${productId}/file`,
      (progress) => {
        setUploadProgress((prev) => ({ ...prev, mainFile: progress }));
        onProgress?.(progress);
      }
    );
    setFiles((prev) => ({ ...prev, mainFile: file, mainFileKey: key }));
    return key;
  };

  const uploadPreview = async (file: File, onProgress?: (progress: number) => void) => {
    setUploadProgress((prev) => ({ ...prev, preview: 0 }));
    const key = await awsService.uploadFile(
      file,
      `products/${productId}/preview`,
      (progress) => {
        setUploadProgress((prev) => ({ ...prev, preview: progress }));
        onProgress?.(progress);
      }
    );
    setFiles((prev) => ({ ...prev, preview: file, previewKey: key }));
    return key;
  };

  const uploadGalleryImage = async (file: File, onProgress?: (progress: number) => void) => {
    const key = await awsService.uploadFile(
      file,
      `products/${productId}/gallery-${Date.now()}`,
      onProgress
    );
    return key;
  };

  const handleSubmit = async () => {
    // Check if seller has Stripe account before allowing product creation
    if (user?.role === 'SELLER') {
      if (!stripeAccountStatus?.accountId || stripeAccountStatus?.status !== 'ENABLED' || !stripeAccountStatus?.onboardingCompleted) {
        const shouldProceed = window.confirm(
          'You need to link your Stripe Connect account to receive payments from product sales. ' +
          'Would you like to set it up now? (You can still create the product, but it won\'t be purchasable until Stripe is linked)'
        );
        
        if (shouldProceed) {
          onClose();
          navigate('/seller/onboarding');
          return;
        }
        // Allow them to continue anyway (product will be created but not purchasable)
      }
    }

    setIsSubmitting(true);

    try {
      // Prepare media array
      const media: MediaDto[] = [];

      // Add thumbnail
      if (files.thumbnail && files.thumbnailKey) {
        media.push({
          key: files.thumbnailKey,
          fileName: files.thumbnail.name,
          fileUrl: `https://markteplacemedia.s3.eu-north-1.amazonaws.com/${files.thumbnailKey}`,
          fileSize: files.thumbnail.size,
          mimeType: files.thumbnail.type,
          entityType: 'PRODUCT_THUMBNAIL',
          description: 'Product thumbnail',
        });
      }

      // Add gallery images
      for (const key of galleryKeys) {
        media.push({
          key,
          fileName: 'Gallery image',
          fileUrl: `https://markteplacemedia.s3.eu-north-1.amazonaws.com/${key}`,
          fileSize: 0,
          mimeType: 'image/jpeg',
          entityType: 'PRODUCT_GALLERY',
        });
      }

      // Create product
      const productData: CreateProductDto = {
        title: formData.title,
        description: formData.description,
        price: parseFloat(formData.price),
        category: formData.category,
        sector: formData.sector,
        tags: formData.tags.split(',').map((t) => t.trim()).filter(Boolean),
        fileUrl: files.mainFileKey,
        previewUrl: files.previewKey || undefined,
        thumbnail: files.thumbnailKey || undefined,
        media,
      };

      await productService.createProduct(productData);

      setShowSuccess(true);
      setTimeout(() => {
        onSuccess?.();
        handleClose();
      }, 2000);
    } catch (error) {
      console.error('Error creating product:', error);
      alert('Failed to create product. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setFormData({
      title: '',
      description: '',
      price: '',
      category: '',
      sector: '',
      tags: '',
    });
    setFiles({
      thumbnail: null,
      mainFile: null,
      preview: null,
      thumbnailKey: '',
      mainFileKey: '',
      previewKey: '',
    });
    setUploadProgress({
      thumbnail: 0,
      mainFile: 0,
      preview: 0,
    });
    setGalleryKeys([]);
    setShowSuccess(false);
    onClose();
  };

  const canProceedStep1 = formData.title && formData.description && formData.price && formData.category && formData.sector;
  const canProceedStep2 = files.thumbnailKey && files.mainFileKey;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={handleClose} />
        
        <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          {/* Success Overlay */}
          {showSuccess && (
            <div className="absolute inset-0 bg-white bg-opacity-95 flex items-center justify-center z-10">
              <div className="text-center">
                <CheckCircleIcon className="h-20 w-20 text-green-500 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-secondary-900 mb-2">
                  Product Created Successfully!
                </h3>
                <p className="text-secondary-600">Redirecting...</p>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-white">Add New Product</h2>
                <p className="text-primary-100 mt-1">
                  Step {step} of 3 - {step === 1 ? 'Basic Info' : step === 2 ? 'Files & Images' : 'Review & Publish'}
                </p>
              </div>
              <button
                onClick={handleClose}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Progress Bar */}
            <div className="mt-4 bg-primary-800 bg-opacity-30 rounded-full h-2">
              <div
                className="bg-white rounded-full h-2 transition-all duration-300"
                style={{ width: `${(step / 3) * 100}%` }}
              />
            </div>
          </div>

          {/* Content */}
          <div className="p-8 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
            {/* Stripe Account Warning for Sellers */}
            {step === 1 && user?.role === 'SELLER' && stripeAccountStatus && 
             (!stripeAccountStatus.accountId || stripeAccountStatus.status !== 'ENABLED' || !stripeAccountStatus.onboardingCompleted) && (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start">
                  <CreditCardIcon className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-yellow-800 mb-1">
                      Payment Setup Required
                    </h3>
                    <p className="text-sm text-yellow-700 mb-3">
                      To receive payments from product sales, you need to link your Stripe Connect account. 
                      You'll receive 90% of each sale, with 10% going to the platform.
                    </p>
                    <Button
                      size="sm"
                      onClick={() => {
                        onClose();
                        navigate('/seller/onboarding');
                      }}
                      className="bg-yellow-600 hover:bg-yellow-700 text-white"
                    >
                      Set Up Stripe Account
                      <ArrowRightIcon className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-6 animate-fadeIn">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Product Title <span className="text-red-500">*</span>
                  </label>
                  <Input
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="e.g., Comprehensive Safeguarding Policy Pack"
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={4}
                    placeholder="Describe your product in detail..."
                    className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Price (£) <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleInputChange}
                      placeholder="29.99"
                      step="0.01"
                      min="0"
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Tags (comma-separated)
                    </label>
                    <Input
                      name="tags"
                      value={formData.tags}
                      onChange={handleInputChange}
                      placeholder="policies, safeguarding, ofsted"
                      className="w-full"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Category <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      disabled={isLoadingCategories}
                      className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-secondary-100 disabled:cursor-not-allowed"
                    >
                      <option value="">
                        {isLoadingCategories ? 'Loading categories...' : 'Select category'}
                      </option>
                      {categories.map((cat) => {
                        const enumValue = categoryNameToEnum[cat.name];
                        return (
                          <option key={cat.id} value={enumValue || cat.name}>
                            {cat.name}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Sector <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="sector"
                      value={formData.sector}
                      onChange={handleInputChange}
                      disabled={isLoadingSectors}
                      className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-secondary-100 disabled:cursor-not-allowed"
                    >
                      <option value="">
                        {isLoadingSectors ? 'Loading sectors...' : 'Select sector'}
                      </option>
                      {sectors.map((sec) => {
                        const enumValue = sectorNameToEnum[sec.name];
                        return (
                          <option key={sec.id} value={enumValue || sec.name}>
                            {sec.name}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-6 animate-fadeIn">
                <ImageUploader
                  label="Product Thumbnail"
                  onUpload={uploadThumbnail}
                  required
                  aspectRatio="16/9"
                  helperText="This will be the main image shown in listings"
                />

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Main Product File <span className="text-red-500">*</span>
                  </label>
                  <div className="space-y-2">
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.zip"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) uploadMainFile(file);
                      }}
                      className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      disabled={uploadProgress.mainFile > 0 && uploadProgress.mainFile < 100}
                    />
                    {uploadProgress.mainFile > 0 && uploadProgress.mainFile < 100 && (
                      <div className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-secondary-600">Uploading...</span>
                          <span className="text-primary-600 font-medium">{uploadProgress.mainFile}%</span>
                        </div>
                        <div className="w-full bg-secondary-200 rounded-full h-2">
                          <div
                            className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${uploadProgress.mainFile}%` }}
                          ></div>
                        </div>
                      </div>
                    )}
                    {uploadProgress.mainFile === 100 && (
                      <div className="flex items-center text-green-600 text-sm">
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        Uploaded successfully
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-secondary-500 mt-1">
                    PDF, DOC, DOCX, or ZIP file
                  </p>
                </div>

                <ImageUploader
                  label="Preview Image (Optional)"
                  onUpload={uploadPreview}
                  aspectRatio="16/9"
                  helperText="Show a preview of your product content"
                />

                <MultipleImageUploader
                  label="Gallery Images (Optional)"
                  onUpload={uploadGalleryImage}
                  onImagesChange={setGalleryKeys}
                  maxFiles={5}
                  helperText="Add up to 5 additional images"
                />
              </div>
            )}

            {step === 3 && (
              <div className="space-y-6 animate-fadeIn">
                <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-6 border border-primary-200">
                  <h3 className="text-lg font-bold text-primary-900 mb-4">
                    Review Your Product
                  </h3>
                  
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-secondary-600">Title:</span>
                      <span className="font-medium text-secondary-900">{formData.title}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-secondary-600">Price:</span>
                      <span className="font-medium text-secondary-900">£{formData.price}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-secondary-600">Category:</span>
                      <span className="font-medium text-secondary-900">
                        {categories.find((c) => {
                          const enumValue = categoryNameToEnum[c.name];
                          return enumValue === formData.category || c.name === formData.category;
                        })?.name || formData.category}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-secondary-600">Sector:</span>
                      <span className="font-medium text-secondary-900">
                        {sectors.find((s) => {
                          const enumValue = sectorNameToEnum[s.name];
                          return enumValue === formData.sector || s.name === formData.sector;
                        })?.name || formData.sector}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-secondary-600">Files:</span>
                      <span className="font-medium text-secondary-900">
                        {[files.thumbnailKey, files.mainFileKey, files.previewKey, ...galleryKeys]
                          .filter(Boolean).length}{' '}
                        files
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-800">
                    ⚠️ Your product will be submitted for review. It will be visible in the marketplace once approved by our team.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-secondary-50 px-8 py-4 flex items-center justify-between border-t border-secondary-200">
            <div>
              {step > 1 && (
                <Button
                  variant="outline"
                  onClick={() => setStep(step - 1)}
                  disabled={isSubmitting}
                >
                  Back
                </Button>
              )}
            </div>

            <div className="flex items-center space-x-3">
              <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </Button>
              
              {step < 3 ? (
                <Button
                  onClick={() => setStep(step + 1)}
                  disabled={
                    isSubmitting ||
                    (step === 1 && !canProceedStep1) ||
                    (step === 2 && !canProceedStep2)
                  }
                >
                  Continue
                </Button>
              ) : (
                <Button onClick={handleSubmit} disabled={isSubmitting}>
                  {isSubmitting ? 'Creating...' : 'Create Product'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

